import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDoc,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import BookRecommendations from '../components/BookRecommendations';
import './Friends.css';

function Friends() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchFriendsData();
  }, [currentUser]);

  const fetchFriendsData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // Fetch friends list
      await fetchFriends();
      
      // Fetch incoming friend requests
      await fetchFriendRequests();
      
      // Fetch sent friend requests
      await fetchSentRequests();
    } catch (error) {
      console.error('Error fetching friends data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFriends = async () => {
    try {
      const friendsQuery = query(
        collection(db, 'friendships'),
        where('users', 'array-contains', currentUser.uid),
        where('status', '==', 'accepted')
      );
      const querySnapshot = await getDocs(friendsQuery);
      
      const friendsData = [];
      for (const docSnap of querySnapshot.docs) {
        const friendship = docSnap.data();
        const friendId = friendship.users.find(id => id !== currentUser.uid);
        
        // Get friend's profile
        const friendProfile = await getDoc(doc(db, 'userProfiles', friendId));
        if (friendProfile.exists()) {
          friendsData.push({
            id: friendId,
            ...friendProfile.data(),
            friendshipId: docSnap.id
          });
        }
      }
      
      setFriends(friendsData);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const requestsQuery = query(
        collection(db, 'friendRequests'),
        where('toUserId', '==', currentUser.uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(requestsQuery);
      
      const requestsData = [];
      for (const docSnap of querySnapshot.docs) {
        const request = docSnap.data();
        
        // Get sender's profile
        const senderProfile = await getDoc(doc(db, 'userProfiles', request.fromUserId));
        if (senderProfile.exists()) {
          requestsData.push({
            id: docSnap.id,
            ...request,
            senderProfile: senderProfile.data()
          });
        }
      }
      
      setFriendRequests(requestsData);
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  };

  const fetchSentRequests = async () => {
    try {
      const sentQuery = query(
        collection(db, 'friendRequests'),
        where('fromUserId', '==', currentUser.uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(sentQuery);
      
      const sentData = [];
      for (const docSnap of querySnapshot.docs) {
        const request = docSnap.data();
        
        // Get recipient's profile
        const recipientProfile = await getDoc(doc(db, 'userProfiles', request.toUserId));
        if (recipientProfile.exists()) {
          sentData.push({
            id: docSnap.id,
            ...request,
            recipientProfile: recipientProfile.data()
          });
        }
      }
      
      setSentRequests(sentData);
    } catch (error) {
      console.error('Error fetching sent requests:', error);
    }
  };

  const searchUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      // Search by display name or email
      const usersQuery = query(
        collection(db, 'userProfiles'),
        limit(20)
      );
      const querySnapshot = await getDocs(usersQuery);
      
      const results = [];
      querySnapshot.docs.forEach(docSnap => {
        const profile = docSnap.data();
        const userId = docSnap.id;
        
        // Skip current user
        if (userId === currentUser.uid) return;
        
        // Check if already friends
        const isFriend = friends.some(friend => friend.id === userId);
        const hasPendingRequest = friendRequests.some(req => req.fromUserId === userId) ||
                                 sentRequests.some(req => req.toUserId === userId);
        
        // Simple search matching
        const displayName = profile.displayName || '';
        const email = profile.email || '';
        
        if (displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.toLowerCase().includes(searchTerm.toLowerCase())) {
          results.push({
            id: userId,
            ...profile,
            isFriend,
            hasPendingRequest
          });
        }
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  const sendFriendRequest = async (userId) => {
    try {
      await addDoc(collection(db, 'friendRequests'), {
        fromUserId: currentUser.uid,
        toUserId: userId,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Update search results
      setSearchResults(prev => 
        prev.map(user => 
          user.id === userId 
            ? { ...user, hasPendingRequest: true }
            : user
        )
      );
      
      // Refresh sent requests
      fetchSentRequests();
    } catch (error) {
      console.error('Error sending friend request:', error);
    }
  };

  const acceptFriendRequest = async (requestId, fromUserId) => {
    try {
      // Update request status
      await updateDoc(doc(db, 'friendRequests', requestId), {
        status: 'accepted',
        updatedAt: new Date()
      });
      
      // Create friendship record
      await addDoc(collection(db, 'friendships'), {
        users: [currentUser.uid, fromUserId],
        status: 'accepted',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Refresh all data
      fetchFriendsData();
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  const rejectFriendRequest = async (requestId) => {
    try {
      await updateDoc(doc(db, 'friendRequests', requestId), {
        status: 'rejected',
        updatedAt: new Date()
      });
      
      // Remove from pending requests
      setFriendRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };

  const cancelFriendRequest = async (requestId) => {
    try {
      await deleteDoc(doc(db, 'friendRequests', requestId));
      
      // Remove from sent requests
      setSentRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('Error cancelling friend request:', error);
    }
  };

  const removeFriend = async (friendshipId) => {
    if (window.confirm('Are you sure you want to remove this friend?')) {
      try {
        await deleteDoc(doc(db, 'friendships', friendshipId));
        
        // Refresh friends list
        fetchFriends();
      } catch (error) {
        console.error('Error removing friend:', error);
      }
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounced search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  const tabs = [
    { key: 'friends', label: 'Friends', icon: '👥', badge: friends.length > 0 ? friends.length : null },
    { key: 'recommendations', label: 'Book Recs', icon: '📚', badge: null },
    { key: 'requests', label: 'Requests', icon: '📨', badge: friendRequests.length > 0 ? friendRequests.length : null },
    { key: 'sent', label: 'Sent', icon: '📤', badge: sentRequests.length > 0 ? sentRequests.length : null },
    { key: 'search', label: 'Find Friends', icon: '🔍', badge: null }
  ];

  if (loading) {
    return (
      <div className="friends-loading">
        <div className="loading-spinner"></div>
        <p>Loading friends...</p>
      </div>
    );
  }

  return (
    <div className="friends-page">
      <div className="container">
        <div className="friends-header">
          <h1>👥 Friends</h1>
          <p>Connect with fellow readers and share your reading journey</p>
        </div>

        {/* Tab Navigation */}
        <nav className="friends-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`friends-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.badge && <span className="tab-badge">{tab.badge}</span>}
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className="friends-content">
          {activeTab === 'friends' && (
            <div className="friends-list">
              {friends.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">👥</div>
                  <h3>No friends yet</h3>
                  <p>Start connecting with other readers to build your reading community!</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setActiveTab('search')}
                  >
                    Find Friends
                  </button>
                </div>
              ) : (
                <div className="friends-grid">
                  {friends.map(friend => (
                    <div key={friend.id} className="friend-card">
                      <div className="friend-avatar">
                        {friend.displayName ? friend.displayName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="friend-info">
                        <h4>{friend.displayName || 'Anonymous Reader'}</h4>
                        <p>{friend.email}</p>
                        {friend.bio && <p className="friend-bio">{friend.bio}</p>}
                      </div>
                      <div className="friend-actions">
                        <button className="btn btn-secondary btn-sm">
                          💬 Message
                        </button>
                        <button 
                          className="btn btn-outline btn-sm"
                          onClick={() => removeFriend(friend.friendshipId)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'recommendations' && (
            <BookRecommendations />
          )}

          {activeTab === 'requests' && (
            <div className="friend-requests">
              {friendRequests.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📨</div>
                  <h3>No pending requests</h3>
                  <p>Friend requests will appear here when other users want to connect with you.</p>
                </div>
              ) : (
                <div className="requests-list">
                  {friendRequests.map(request => (
                    <div key={request.id} className="request-card">
                      <div className="request-avatar">
                        {request.senderProfile.displayName ? 
                          request.senderProfile.displayName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="request-info">
                        <h4>{request.senderProfile.displayName || 'Anonymous Reader'}</h4>
                        <p>{request.senderProfile.email}</p>
                        <p className="request-date">
                          Sent {new Date(request.createdAt.toDate()).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="request-actions">
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => acceptFriendRequest(request.id, request.fromUserId)}
                        >
                          ✓ Accept
                        </button>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => rejectFriendRequest(request.id)}
                        >
                          ✕ Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sent' && (
            <div className="sent-requests">
              {sentRequests.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📤</div>
                  <h3>No sent requests</h3>
                  <p>Friend requests you send will appear here.</p>
                </div>
              ) : (
                <div className="requests-list">
                  {sentRequests.map(request => (
                    <div key={request.id} className="request-card">
                      <div className="request-avatar">
                        {request.recipientProfile.displayName ? 
                          request.recipientProfile.displayName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="request-info">
                        <h4>{request.recipientProfile.displayName || 'Anonymous Reader'}</h4>
                        <p>{request.recipientProfile.email}</p>
                        <p className="request-date">
                          Sent {new Date(request.createdAt.toDate()).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="request-actions">
                        <button 
                          className="btn btn-outline btn-sm"
                          onClick={() => cancelFriendRequest(request.id)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'search' && (
            <div className="user-search">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search for friends by name or email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                {searching && <div className="search-spinner">🔍</div>}
              </div>
              
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(user => (
                    <div key={user.id} className="search-result-card">
                      <div className="result-avatar">
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="result-info">
                        <h4>{user.displayName || 'Anonymous Reader'}</h4>
                        <p>{user.email}</p>
                        {user.bio && <p className="result-bio">{user.bio}</p>}
                      </div>
                      <div className="result-actions">
                        {user.isFriend ? (
                          <span className="friend-status">✓ Friends</span>
                        ) : user.hasPendingRequest ? (
                          <span className="pending-status">📤 Request Sent</span>
                        ) : (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => sendFriendRequest(user.id)}
                          >
                            👥 Add Friend
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {searchQuery && searchResults.length === 0 && !searching && (
                <div className="no-results">
                  <p>No users found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Friends;